import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13 } from "styled-components";
import type { SanityArticleCard } from "@ouster/shared";
import React from "react";
import useSWRInfinite from "swr/infinite";
import { type Fetcher } from "swr";
import { hasItems } from "~/features/app/utils";
import { BlogArticleCard } from "~/features/blog/article-card";
import { buildArticlesUrl } from "~/features/blog/articles-url";
import { NoResults } from "~/features/filter/no-results";
import { Grid } from "~/features/ui/grid";
import { LoadMore } from "~/features/ui/load-more";
import { useInfiniteScroll } from "~/features/ui/use-infinite-scroll";
import { Wrap } from "~/features/ui/wrap";
type ArticlesPage = {
  success?: boolean;
  data?: SanityArticleCard[];
  total?: number;
};
type Props = {
  limit: number;
  total: number;
  initialArticles: SanityArticleCard[];
  featuredArticle?: SanityArticleCard;
  middleBlocks: React.ReactNode;
  activeCategory?: string;
  activeIndustry?: string;
  onClearFilters: () => void;
};
const fetcher: Fetcher<ArticlesPage, string> = async (url: string) => {
  const response = await fetch(url);
  const body = (await response.json()) as ArticlesPage;
  if (!response.ok || body.success !== true) {
    throw new Error("Could not load articles. Please try again.");
  }
  return body;
};
export function BlogArticlesGrid(props: Props) {
  const {
    limit,
    initialArticles,
    total,
    featuredArticle,
    middleBlocks,
    activeCategory,
    activeIndustry,
    onClearFilters
  } = props;
  const hasActiveFilters = Boolean(activeCategory || activeIndustry);
  const {
    data,
    error,
    isLoading,
    mutate,
    size,
    setSize
  } = useSWRInfinite(pageIndex => buildArticlesUrl(pageIndex * limit, limit, activeCategory, activeIndustry), fetcher, {
    revalidateFirstPage: hasActiveFilters,
    revalidateOnMount: hasActiveFilters,
    fallbackData: hasActiveFilters ? undefined : [{
      success: true,
      data: initialArticles,
      total
    }],
    parallel: true
  });
  const pages = React.useMemo(() => data ?? [], [data]);
  const articles = React.useMemo(() => {
    return pages.flatMap(page => page.data ?? []);
  }, [pages]);
  const isLoadingMore = isLoading || size > 0 && pages[size - 1] === undefined;
  const lastPage = pages[pages.length - 1];
  const isLastPageNotFull = (lastPage?.data?.length ?? 0) < limit;
  const activeTotal = pages[0]?.total ?? total;
  const hasMore = articles.length < activeTotal && !isLastPageNotFull;

  // if there's any featured article, move it to the top
  const firstBatch = React.useMemo(() => {
    const batch = [...articles.slice(0, featuredArticle?._id ? 7 : 9)];
    if (featuredArticle) {
      // Check if the featured article is already in the batch
      const isAlreadyInBatch = batch.some(article => article._id === featuredArticle._id);
      if (!isAlreadyInBatch) {
        // If it's not in the batch, insert it at index 3
        batch.splice(3, 0, featuredArticle);
      }
    }
    return batch;
  }, [articles, featuredArticle]);
  const secondBatch = React.useMemo(() => {
    const renderedIds = new Set(firstBatch.map(article => article._id).filter(Boolean));
    return articles.filter(article => !renderedIds.has(article._id));
  }, [articles, firstBatch]);
  const loadMoreRef = useInfiniteScroll({
    hasMore: hasMore && !error,
    isLoading: isLoadingMore,
    onLoadMore: () => setSize(prev => prev + 1)
  });
  const isInitialLoading = !hasItems(articles) && isLoadingMore && !error;
  const hasNoResults = !hasItems(articles) && !isLoadingMore && !error;
  const statusMessage = error ? "Articles could not be loaded." : isLoadingMore ? "Loading articles." : hasNoResults ? "No articles found." : `${activeTotal} ${activeTotal === 1 ? "article" : "articles"} found. ${articles.length} shown.`;
  return <div data-theme="light" role="region" aria-label="Blog articles" aria-busy={isLoadingMore}>
      <_StyledP role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </_StyledP>

      {isInitialLoading ? <_StyledDiv>Loading articles…</_StyledDiv> : hasNoResults ? <_StyledDiv2>
          <_StyledWrap>
            <_StyledDiv3>
              <NoResults onClear={onClearFilters} />
            </_StyledDiv3>
          </_StyledWrap>
        </_StyledDiv2> : <>
          <_StyledWrap2>
            <div>
              {hasItems(firstBatch) && <_StyledGrid $_css={[{
            "gap": "var(--spacing-md)",
            "rowGap": "var(--spacing-2xl)",
            "--tw-bg-opacity": "1",
            "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
            "padding": "var(--spacing-md)",
            "@media (min-width: 1024px)": {
              "paddingLeft": "var(--spacing-xl)",
              "paddingRight": "var(--spacing-xl)",
              "paddingTop": "var(--spacing-2xl)",
              "paddingBottom": "var(--spacing-2xl)"
            }
          }]}>
                  {firstBatch.map((article, index) => {
              const isFeatured = Boolean(featuredArticle?._id) && featuredArticle?._id === article._id;
              return <_StyledDiv4 key={index} $_css2={[{
                "gridColumn": "1 / -1",
                "@media (min-width: 1024px)": {
                  "gridColumn": "span 4 / span 4"
                }
              }, isFeatured && {
                "position": "relative",
                "borderRadius": "4px",
                "backgroundColor": "var(--bg-tertiary)",
                "padding": "var(--spacing-lg)",
                "@media (min-width: 1024px)": {
                  "gridColumn": "span 8 / span 8"
                }
              }]}>
                        <BlogArticleCard article={article} featured={isFeatured} size={isFeatured ? "large" : "regular"} ratio={isFeatured ? "fill" : "3/2"} />
                      </_StyledDiv4>;
            })}
                </_StyledGrid>}
              {middleBlocks}
              {hasItems(secondBatch) && <_StyledGrid2 $_css3={[{
            "gap": "var(--spacing-md)",
            "rowGap": "var(--spacing-2xl)",
            "borderTopWidth": "1px",
            "borderColor": "var(--stroke-primary)",
            "--tw-bg-opacity": "1",
            "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
            "padding": "var(--spacing-md)",
            "@media (min-width: 1024px)": {
              "paddingLeft": "var(--spacing-xl)",
              "paddingRight": "var(--spacing-xl)",
              "paddingTop": "var(--spacing-2xl)",
              "paddingBottom": "var(--spacing-2xl)"
            }
          }]}>
                  {secondBatch.map((article, index) => <_StyledDiv5 key={index} $_css4={[{
              "gridColumn": "1 / -1",
              "@media (min-width: 1024px)": {
                "gridColumn": "span 4 / span 4"
              }
            }]}>
                      <BlogArticleCard article={article} />
                    </_StyledDiv5>)}
                </_StyledGrid2>}
            </div>
          </_StyledWrap2>

          {error && <_StyledDiv6 role="alert">
              <p>{error.message}</p>
              <_StyledButton onClick={() => mutate()} type="button">
                Try again
              </_StyledButton>
            </_StyledDiv6>}

          {hasMore && !error && <_StyledDiv7 ref={loadMoreRef} $_css5={[{
        "borderTopWidth": "1px",
        "borderColor": "var(--stroke-primary)",
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
      }]}>
              <LoadMore isLoading={isLoadingMore} onClick={() => setSize(prev => prev + 1)} />
            </_StyledDiv7>}
        </>}
    </div>;
}
var _StyledP = _styled("p").withConfig({
  componentId: "sc-sf-1fke6y9-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1fke6y9-1"
})({
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
  "padding": "2.4rem",
  "textAlign": "center"
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1fke6y9-2"
})({
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
});
var _StyledWrap = _styled4(Wrap).withConfig({
  componentId: "sc-sf-1fke6y9-3"
})({
  "borderWidth": "0px"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1fke6y9-4"
})({
  "padding": "2.4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});
var _StyledWrap2 = _styled6(Wrap).withConfig({
  componentId: "sc-sf-1fke6y9-5"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled7(Grid).withConfig({
  componentId: "sc-sf-1fke6y9-6"
})`${p => p.$_css}`;
var _StyledDiv4 = _styled8("div").withConfig({
  componentId: "sc-sf-1fke6y9-7"
})`${p => p.$_css2}`;
var _StyledGrid2 = _styled9(Grid).withConfig({
  componentId: "sc-sf-1fke6y9-8"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled10("div").withConfig({
  componentId: "sc-sf-1fke6y9-9"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled11("div").withConfig({
  componentId: "sc-sf-1fke6y9-10"
})({
  "borderTopWidth": "1px",
  "borderColor": "var(--stroke-primary)",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
  "padding": "2.4rem",
  "textAlign": "center"
});
var _StyledButton = _styled12("button").withConfig({
  componentId: "sc-sf-1fke6y9-11"
})({
  "marginTop": "1.2rem",
  "textDecorationLine": "underline"
});
var _StyledDiv7 = /*#__PURE__*/_styled13("div").withConfig({
  displayName: "blog-articles-grid___StyledDiv7",
  componentId: "sc-17chfk8-0"
})(["", ""], p => p.$_css5);